import Vue from 'vue'
import { createHelpers } from 'vuex-map-fields'
import moment from 'moment'
import _ from 'lodash'

import {
  // GET_APPLICATION_INFORMATION
  GET_APPLICATION_INFORMATION_REQUEST,
  GET_APPLICATION_INFORMATION_SUCCESS,
  GET_APPLICATION_INFORMATION_FAILURE,

  // GET_CONSENT_INFORMATION
  GET_CONSENT_INFORMATION_REQUEST,
  GET_CONSENT_INFORMATION_SUCCESS,
  GET_CONSENT_INFORMATION_FAILURE,

  // UPDATE_APPLICATION_INFORMATION
  UPDATE_APPLICATION_INFORMATION_REQUEST,
  UPDATE_APPLICATION_INFORMATION_SUCCESS,
  UPDATE_APPLICATION_INFORMATION_FAILURE,

  // UPDATE_PAYER_INFORMATION
  UPDATE_PAYER_INFORMATION_REQUEST,
  UPDATE_PAYER_INFORMATION_SUCCESS,
  UPDATE_PAYER_INFORMATION_FAILURE,

  // UPDATE_MAIN_PAYER
  UPDATE_MAIN_PAYER_REQUEST,
  UPDATE_MAIN_PAYER_SUCCESS,
  UPDATE_MAIN_PAYER_FAILURE,

  // UPDATE_APPLICATION_COMPLETED_STATUS
  UPDATE_APPLICATION_COMPLETED_STATUS_REQUEST,
  UPDATE_APPLICATION_COMPLETED_STATUS_SUCCESS,
  UPDATE_APPLICATION_COMPLETED_STATUS_FAILURE,

  // UPDATE_CONSENT_INSURED_INFORMATION
  UPDATE_CONSENT_INSURED_INFORMATION_REQUEST,
  UPDATE_CONSENT_INSURED_INFORMATION_SUCCESS,
  UPDATE_CONSENT_INSURED_INFORMATION_FAILURE,

  // UPDATE_CONSENT_PAYER_INFORMATION
  UPDATE_CONSENT_PAYER_INFORMATION_REQUEST,
  UPDATE_CONSENT_PAYER_INFORMATION_SUCCESS,
  UPDATE_CONSENT_PAYER_INFORMATION_FAILURE,

  // GET_TMB_ACCOUNT_INFORMATION
  GET_TMB_ACCOUNT_INFORMATION_REQUEST,
  GET_TMB_ACCOUNT_INFORMATION_SUCCESS,
  GET_TMB_ACCOUNT_INFORMATION_FAILURE,

  // SEND_OTP_VERIFICATION
  SEND_OTP_VERIFICATION_REQUEST,
  SEND_OTP_VERIFICATION_SUCCESS,
  SEND_OTP_VERIFICATION_FAILURE,

  // VERIFY_OTP_VERIFICATION
  VERIFY_OTP_VERIFICATION_REQUEST,
  VERIFY_OTP_VERIFICATION_SUCCESS,
  VERIFY_OTP_VERIFICATION_FAILURE,
} from '@/lib/mutationTypes'
import router from '@/router'
import { DATE_FORMAT, validationRule } from '@/lib/global'
import API, { HOMEPAGE_URL } from '@/lib/api'
import { generateAuthorizationRequestHeader } from '@/lib/services'

const { getApplicationFields, updateApplicationFields } = createHelpers({
  getterType: 'getApplicationFields',
  mutationType: 'updateApplicationFields',
})

const state = {
  data: {
    applicationId: null,
    applicationInformation: null,
    healthQuestion: {
      isValidated: false,
      isPassValidate: false,
      question: {
        q1: null,
        q2: null,
        q3: null,
        q4: null,
        q5: null,
      },
    },
    insuredAddress: {
      isSameAddress: false,
      policySendMethod: '',
      insuredInformation: {
        id: '',
        namePrefix: '',
        firstname: '',
        lastname: '',
        idType: 1,
        idNumber: '',
        occupation: '',
        dob: '',
        age: '',
        gender: '',
        email: '',
        height: '',
        weight: '',
      },
      currentAddress: {
        isApiData: false,
        address: '',
        subdistrict: '',
        district: '',
        province: '',
        country: 'ประเทศไทย',
        postalCode: '',
        homePhone: '',
        mobilePhone: '',
      },
      deliveryAddress: {
        isApiData: false,
        address: '',
        subdistrict: '',
        district: '',
        province: '',
        country: 'ประเทศไทย',
        postalCode: '',
        homePhone: '',
        mobilePhone: '',
      },
    },
    insurancePlanStartDate: '',
    insuranceInformation: {
      planId: '',
      planName: '',
      coverageCode: '',
      totalBeforeDeduct: 0,
      finalTotal: 0,
      deductible: {
        value: 0,
        discount: 0,
      },
      familyDiscount: {
        percentage: 0,
        discount: 0,
      },
    },
    beneficiary: {
      isBeneficiaryLegalHeir: false,
      firstPerson: {
        namePrefix: '',
        firstname: '',
        lastname: '',
        relationship: null,
        percentage: null,
        isNull: false,
      },
      secondPerson: {
        namePrefix: '',
        firstname: '',
        lastname: '',
        relationship: null,
        percentage: null,
        isNull: false,
      },
    },
    foreigner: {
      isHasThailandResidency: null,
      countryOfResidency: '',
    },
    paymentMethod: {
      payerInformation: {
        payerId: '',
        namePrefix: '',
        firstname: '',
        lastname: '',
        relationship: '',
        phone: '',
      },
      method: null,
      isCombinePayment: false,
      tmbMethodInformation: {
        accountNumber: null,
        accountName: null,
        accountStatus: null,
        accountType: null,
        productCode: null,
        paymentRefId: null,
        isAccountNumberCorrected: null,
        isAccountPayable: null,
        isShowOtp: false,
        isVerificationCompleted: false,
        otp: {
          otpCode: null,
          referenceCode: null,
          token: null,
          expiryDateTime: null,
          isOtpStatusActivated: false,
          otpAttempt: 3,
          timeoutOtp: null,
        },
      },
    },
    isAutoRenewPolicy: null,
    reclaimTax: {
      isReclaim: null,
      taxPayerNumber: '',
    },
    consentInformation: null,
  },
  validation: {
    rules: {
      q1: validationRule.healthQuestion,
      q2: validationRule.healthQuestion,
      q3: validationRule.healthQuestion,
      q4: validationRule.healthQuestion,
      q5: validationRule.healthQuestion,
      deliveryMethod: 'required',
      deliveryAddress: '',
      deliverySubdistrict: '',
      deliveryDistrict: '',
      deliveryProvince: '',
      deliveryCountry: '',
      deliveryPostalCode: '',
      deliveryMobilePhone: '',
      effectiveDate: 'required',
      payerRelationship: '',
      beneficiaryFirstPersonRelationship: '',
      beneficiaryFirstPersonPercentage: '',
      beneficiarySecondPersonPercentage: '',
      beneficiarySecondPersonRelationship: '',
      isHasThailandResidency: '',
      countryOfResidency: '',
      paymentMethod: 'required',
      isAutoRenewPolicy: 'required',
      isReclaimTax: 'required',
    },
    errors: null,
  },
  isDisabledForm: false,
  isLoading: false,
  isError: false,
}

const mutations = {
  // GET_APPLICATION_INFORMATION
  [GET_APPLICATION_INFORMATION_REQUEST](state) {
    state.isLoading = true
    state.isError = false
  },
  [GET_APPLICATION_INFORMATION_SUCCESS](state, payload) {
    const {
      applicationId,
      insuredPerson,
      application,
      quotation,
      familyQuotation,
      deductible,
      payer,
      payment,
      planPackageScheme,
      pricing,
      beneficiaryPerson1,
      beneficiaryPerson2,
    } = payload

    const {
      personId: insuredId,
      title,
      firstname,
      lastname,
      identityTypeId,
      identityNumber,
      occupation,
      birthDate,
      age,
      sex: gender,
      email,
      height,
      weight,
      mainAddress,
      mainSoiRoad,
      mainTambon,
      mainAmphoe,
      mainProvince,
      mainHomeTel,
      mainMobileTel,
      deliveryAddress,
      deliverySoiRoad,
      deliveryTambon,
      deliveryAmphoe,
      deliveryProvince,
      deliveryHomeTel,
      deliveryMobileTel,
    } = insuredPerson

    const {
      healthAnswers,
      deliveryMethod,
      policyStartDate,
      beneficiaryPersonRelationship1,
      beneficiaryPersonPercentage1,
      beneficiaryPersonRelationship2,
      beneficiaryPersonPercentage2,
      isAutoRenew,
      isAllowTaxExemption,
      taxExemptionId,
      documentProcessStatusId,
      pvApplicationNumber,
      foreignerHasThailandResidency,
      foreignerCountryResidency,
      payerRelationshipId,
      payerMobileTel,
    } = application

    const isDocumentCompleted = Boolean(pvApplicationNumber || documentProcessStatusId > 1)

    state.data.applicationId = applicationId

    // set health answer data
    if (healthAnswers) {
      const healthAnsArr = healthAnswers.split(',')

      state.data.healthQuestion = {
        ...state.data.healthQuestion,
        question: {
          q1: healthAnsArr[0],
          q2: healthAnsArr[1],
          q3: healthAnsArr[2],
          q4: healthAnsArr[3],
          q5: healthAnsArr[4],
        },
      }
    } else {
      state.data.healthQuestion = {
        ...state.data.healthQuestion,
        question: {
          q1: null,
          q2: null,
          q3: null,
          q4: null,
          q5: null,
        },
      }
    }

    // set insured information data
    state.data.insuredAddress.insuredInformation = {
      id: insuredId,
      namePrefix: title.name,
      firstname,
      lastname,
      idType: identityTypeId,
      idNumber: identityNumber,
      occupation: occupation.nameTh,
      dob: moment(birthDate).add(543, 'years').format(DATE_FORMAT),
      age,
      gender,
      email,
      height,
      weight,
    }

    // set insured current address
    state.data.insuredAddress.currentAddress = {
      isApiData: true,
      address: `${mainAddress || ''} ${mainSoiRoad || ''}`,
      subdistrict: _.get(mainTambon, 'id', ''),
      district: _.get(mainAmphoe, 'amphoeId', ''),
      province: _.get(mainProvince, 'id', ''),
      country: 'ประเทศไทย',
      postalCode: _.get(mainTambon, 'postcode', ''),
      homePhone: mainHomeTel,
      mobilePhone: mainMobileTel,
    }

    // set insured delivery address
    state.data.insuredAddress.deliveryAddress = {
      isApiData: true,
      address: `${deliveryAddress || ''} ${deliverySoiRoad || ''}`,
      subdistrict: _.get(deliveryTambon, 'id', ''),
      district: _.get(deliveryAmphoe, 'amphoeId', ''),
      province: _.get(deliveryProvince, 'id', ''),
      country: 'ประเทศไทย',
      postalCode: _.get(deliveryTambon, 'postcode', ''),
      homePhone: deliveryHomeTel,
      mobilePhone: deliveryMobileTel,
    }

    const isMainAddressEmpty = _.chain(state.data.insuredAddress.currentAddress)
      .omit(['isApiData', 'country'])
      .values()
      .every((data) => _.isEmpty(data))
      .value()

    const isEqualAddress = _.isEqual(
      state.data.insuredAddress.currentAddress,
      state.data.insuredAddress.deliveryAddress,
    )

    state.data.insuredAddress.isSameAddress = !isMainAddressEmpty ? isEqualAddress : false

    state.data.insuredAddress.policySendMethod = deliveryMethod || null

    state.data.insurancePlanStartDate = isDocumentCompleted
      ? moment(policyStartDate).add(543, 'years')
      : ''

    const { planId, planName, coverageCode } = planPackageScheme
    const { deduct: deductValue } = deductible

    state.data.insuranceInformation = {
      planId,
      planName,
      coverageCode,
      totalBeforeDeduct: pricing.grossPremium,
      finalTotal: pricing.netPremium,
      deductible: {
        value: deductValue,
        discount: pricing.deductDiscount,
      },
      familyDiscount: {
        percentage: pricing.familyDiscountPercentage,
        discount: pricing.familyDiscount,
      },
    }

    // set beneficiary data
    state.data.beneficiary.isBeneficiaryLegalHeir = quotation.isBeneficiaryLegalHeir

    if (beneficiaryPerson1) {
      const {
        title: firstPersonTitle,
        firstname: firstPersonFirstname,
        lastname: firstPersonLastname,
        ...otherInformationPerson1
      } = beneficiaryPerson1

      state.data.beneficiary.firstPerson = {
        ...state.data.beneficiary.firstPerson,
        ...otherInformationPerson1,
        namePrefix: firstPersonTitle.name,
        firstname: firstPersonFirstname,
        lastname: firstPersonLastname,
      }

      state.validation.rules.beneficiaryFirstPersonRelationship = !quotation.isBeneficiaryLegalHeir
        ? 'required'
        : ''
      state.validation.rules.beneficiaryFirstPersonPercentage = !quotation.isBeneficiaryLegalHeir
        ? 'required'
        : ''
    } else {
      state.data.beneficiary.firstPerson.isNull = true
    }

    state.data.beneficiary.firstPerson.relationship = _.get(
      beneficiaryPersonRelationship1,
      'id',
      null,
    )
    state.data.beneficiary.firstPerson.percentage = beneficiaryPersonPercentage1

    if (beneficiaryPerson2) {
      const {
        title: secondPersonTitle,
        firstname: secondPersonFirstname,
        lastname: secondPersonLastname,
        ...otherInformationPerson2
      } = beneficiaryPerson2

      state.data.beneficiary.secondPerson = {
        ...state.data.beneficiary.secondPerson,
        ...otherInformationPerson2,
        namePrefix: secondPersonTitle.name,
        firstname: secondPersonFirstname,
        lastname: secondPersonLastname,
      }

      state.validation.rules.beneficiarySecondPersonRelationship = !quotation.isBeneficiaryLegalHeir
        ? 'required'
        : ''
      state.validation.rules.beneficiarySecondPersonPercentage = !quotation.isBeneficiaryLegalHeir
        ? 'required'
        : ''
    } else {
      state.data.beneficiary.secondPerson.isNull = true
    }

    state.data.beneficiary.secondPerson.relationship = _.get(
      beneficiaryPersonRelationship2,
      'id',
      null,
    )
    state.data.beneficiary.secondPerson.percentage = beneficiaryPersonPercentage2

    if (beneficiaryPerson1 && !beneficiaryPerson2) {
      state.data.beneficiary.firstPerson.percentage = 100
    } else if (!beneficiaryPerson1 && beneficiaryPerson2) {
      state.data.beneficiary.secondPerson.percentage = 100
    }

    const isTypePassport = identityTypeId === 2

    // set foreigner information data
    if (isTypePassport) {
      state.data.foreigner.isHasThailandResidency = foreignerHasThailandResidency
      state.data.foreigner.countryOfResidency = foreignerCountryResidency
    }

    const isCombinePayment = _.get(familyQuotation, 'isCombinePayment', false)

    if (isCombinePayment) {
      const familyPayerId = _.get(familyQuotation, 'quotations[0].payerId', null)
      const familyPayerNamePrefix = _.get(familyQuotation, 'quotations[0].payerTitle', null)
      const familyPayerFirstname = _.get(familyQuotation, 'quotations[0].payerFirstname', null)
      const familyPayerLastname = _.get(familyQuotation, 'quotations[0].payerLastname', null)
      const familyPayerRelationship = _.get(payerRelationshipId, 'id', null)
      const familyPayerMobileTel = _.get(familyQuotation, 'quotations[0].payerMobileTel', null)

      state.data.paymentMethod.payerInformation = {
        payerId: familyPayerId,
        namePrefix: familyPayerNamePrefix,
        firstname: familyPayerFirstname,
        lastname: familyPayerLastname,
        relationship: familyPayerRelationship,
        phone: familyPayerMobileTel,
      }
    } else {
      const payerId = _.get(payer, 'id', null)
      const payerNamePrefix = _.get(payer, 'title.name', null)
      const payerFirstname = _.get(payer, 'firstname', null)
      const payerLastname = _.get(payer, 'lastname', null)

      state.data.paymentMethod.payerInformation = {
        payerId,
        namePrefix: payerNamePrefix,
        firstname: payerFirstname,
        lastname: payerLastname,
        relationship: _.get(payerRelationshipId, 'id', null),
        phone: payerMobileTel,
      }
    }

    state.data.paymentMethod.method = _.get(payment, 'id', null)
    state.data.paymentMethod.isCombinePayment = isCombinePayment

    if (isCombinePayment) {
      const firstFamilyQuotation = _.head(_.get(familyQuotation, 'quotations', []))
      const familyPaymentMethod = _.get(firstFamilyQuotation, 'paymentId', null)

      state.data.paymentMethod.method = familyPaymentMethod
    }
    // set isAutoRenew policy data
    state.data.isAutoRenewPolicy = isAutoRenew

    state.data.applicationInformation = payload

    // set reclaim tax
    state.data.reclaimTax = {
      isReclaim: isAllowTaxExemption,
      taxPayerNumber: taxExemptionId || identityNumber,
    }

    state.validation.rules.payerRelationship = !quotation.isPaidByInsuredPerson ? 'required' : ''
    state.validation.rules.isHasThailandResidency = isTypePassport ? 'required' : ''
    state.validation.rules.countryOfResidency =
      isTypePassport && foreignerHasThailandResidency ? 'required' : ''

    // set DisabledForm
    state.isDisabledForm = isDocumentCompleted

    state.isLoading = false
    state.isError = false
  },
  [GET_APPLICATION_INFORMATION_FAILURE](state) {
    state.isLoading = false
    state.isError = true
  },

  // GET_CONSENT_INFORMATION
  [GET_CONSENT_INFORMATION_REQUEST](state) {
    state.isLoading = true
    state.isError = false
  },
  [GET_CONSENT_INFORMATION_SUCCESS](state, payload) {
    state.data.consentInformation = _.cloneDeep(payload)
    state.isLoading = false
    state.isError = false
  },
  [GET_CONSENT_INFORMATION_FAILURE](state) {
    state.isLoading = false
    state.isError = true
  },

  // UPDATE_APPLICATION_INFORMATION
  [UPDATE_APPLICATION_INFORMATION_REQUEST](state) {
    state.isLoading = true
    state.isError = false
  },
  [UPDATE_APPLICATION_INFORMATION_SUCCESS](state) {
    state.isLoading = false
    state.isError = false
  },
  [UPDATE_APPLICATION_INFORMATION_FAILURE](state) {
    state.isLoading = false
    state.isError = true
  },

  // UPDATE_PAYER_INFORMATION
  [UPDATE_PAYER_INFORMATION_REQUEST](state) {
    state.isLoading = true
    state.isError = false
  },
  [UPDATE_PAYER_INFORMATION_SUCCESS](state) {
    state.data.applicationInformation.quotation.isPaidByInsuredPerson = false
    state.validation.rules.payerRelationship = 'required'

    state.isLoading = false
    state.isError = false
  },
  [UPDATE_PAYER_INFORMATION_FAILURE](state) {
    state.isLoading = false
    state.isError = true
  },

  // UPDATE_MAIN_PAYER
  [UPDATE_MAIN_PAYER_REQUEST](state) {
    state.isLoading = true
    state.isError = false
  },
  [UPDATE_MAIN_PAYER_SUCCESS](state) {
    state.isLoading = false
    state.isError = false
  },
  [UPDATE_MAIN_PAYER_FAILURE](state) {
    state.isLoading = false
    state.isError = true
  },

  // UPDATE_APPLICATION_COMPLETED_STATUS
  [UPDATE_APPLICATION_COMPLETED_STATUS_REQUEST](state) {
    state.isLoading = true
    state.isError = false
  },
  [UPDATE_APPLICATION_COMPLETED_STATUS_SUCCESS](state) {
    state.isLoading = false
    state.isError = false
  },
  [UPDATE_APPLICATION_COMPLETED_STATUS_FAILURE](state) {
    state.isLoading = false
    state.isError = true
  },

  // UPDATE_CONSENT_INSURED_INFORMATION
  [UPDATE_CONSENT_INSURED_INFORMATION_REQUEST](state) {
    state.isLoading = true
    state.isError = false
  },
  [UPDATE_CONSENT_INSURED_INFORMATION_SUCCESS](state) {
    state.isLoading = false
    state.isError = false
  },
  [UPDATE_CONSENT_INSURED_INFORMATION_FAILURE](state) {
    state.isLoading = false
    state.isError = true
  },

  // UPDATE_CONSENT_PAYER_INFORMATION
  [UPDATE_CONSENT_PAYER_INFORMATION_REQUEST](state) {
    state.isLoading = true
    state.isError = false
  },
  [UPDATE_CONSENT_PAYER_INFORMATION_SUCCESS](state) {
    state.isLoading = false
    state.isError = false
  },
  [UPDATE_CONSENT_PAYER_INFORMATION_FAILURE](state) {
    state.isLoading = false
    state.isError = true
  },

  // GET_TMB_ACCOUNT_INFORMATION
  [GET_TMB_ACCOUNT_INFORMATION_REQUEST]() {
    this._vm.$swal({
      title: 'กรุณารอสักครู่',
      html: '<div class="loader" />',
      showConfirmButton: false,
      allowOutsideClick: false,
    })
  },
  [GET_TMB_ACCOUNT_INFORMATION_SUCCESS](state, payload) {
    const {
      accountInfo: {
        accountTitle,
        accountStatus,
        accountType,
        manipulatedProductCode,
        isAccountAvailable,
      },
      paymentRef,
    } = payload

    state.data.paymentMethod.tmbMethodInformation.accountName = accountTitle
    state.data.paymentMethod.tmbMethodInformation.accountStatus = accountStatus
    state.data.paymentMethod.tmbMethodInformation.accountType = accountType
    state.data.paymentMethod.tmbMethodInformation.productCode = manipulatedProductCode
    state.data.paymentMethod.tmbMethodInformation.paymentRefId = paymentRef
    state.data.paymentMethod.tmbMethodInformation.isAccountNumberCorrected = true
    state.data.paymentMethod.tmbMethodInformation.isAccountPayable = isAccountAvailable

    if (isAccountAvailable) {
      this._vm.$swal({
        title: 'กรุณาตรวจสอบข้อมูลบัญชีของผู้ชำระเบี้ยประกันภัย',
        type: 'info',
        confirmButtonColor: '#00A350',
        confirmButtonText: 'ตกลง',
      })
    } else {
      this._vm.$swal({
        title: 'ประเภทบัญชีของท่านไม่สามารถทำรายการ เพื่อชำระเบี้ยประกันภัยได้',
        type: 'error',
        confirmButtonColor: '#00A350',
        confirmButtonText: 'ตกลง',
      })
    }

    state.data.paymentMethod.tmbMethodInformation.isShowOtp = false
    state.data.paymentMethod.tmbMethodInformation.isVerificationCompleted = false
  },
  [GET_TMB_ACCOUNT_INFORMATION_FAILURE]() {
    this._vm.$swal.close()

    state.data.paymentMethod.tmbMethodInformation.isAccountNumberCorrected = false
  },

  // SEND_OTP_VERIFICATION
  [SEND_OTP_VERIFICATION_REQUEST]() {
    this._vm.$swal({
      title: 'กรุณารอสักครู่',
      html: '<div class="loader" />',
      showConfirmButton: false,
      allowOutsideClick: false,
    })
  },
  [SEND_OTP_VERIFICATION_SUCCESS](state, payload) {
    const { referenceCode, token, expiryDateTime } = payload

    state.data.paymentMethod.tmbMethodInformation.isShowOtp = true
    state.data.paymentMethod.tmbMethodInformation.otp.otpCode = null
    state.data.paymentMethod.tmbMethodInformation.otp.referenceCode = referenceCode
    state.data.paymentMethod.tmbMethodInformation.otp.token = token
    state.data.paymentMethod.tmbMethodInformation.otp.expiryDateTime = expiryDateTime
    state.data.paymentMethod.tmbMethodInformation.otp.isOtpStatusActivated = false
    state.data.paymentMethod.tmbMethodInformation.otp.otpAttempt = 3

    state.data.paymentMethod.tmbMethodInformation.otp.timeoutOtp = setTimeout(() => {
      this._vm
        .$swal({
          type: 'error',
          title:
            'ท่านทำรายการไม่ถูกต้อง หรือไม่ได้ทำรายการภายในระยะเวลาที่กำหนด กรุณาทำรายการใหม่อีกครั้ง',
        })
        .then(() => {
          state.data.paymentMethod.tmbMethodInformation.accountNumber = null
          state.data.paymentMethod.tmbMethodInformation.accountName = null
          state.data.paymentMethod.tmbMethodInformation.accountStatus = null
          state.data.paymentMethod.tmbMethodInformation.accountType = null
          state.data.paymentMethod.tmbMethodInformation.productCode = null
          state.data.paymentMethod.tmbMethodInformation.paymentRefId = null
          state.data.paymentMethod.tmbMethodInformation.isAccountNumberCorrected = null
          state.data.paymentMethod.tmbMethodInformation.isAccountPayable = null
          state.data.paymentMethod.tmbMethodInformation.isShowOtp = false
          state.data.paymentMethod.tmbMethodInformation.otp.otpCode = null
          state.data.paymentMethod.tmbMethodInformation.otp.referenceCode = null
          state.data.paymentMethod.tmbMethodInformation.otp.token = null
          state.data.paymentMethod.tmbMethodInformation.otp.expiryDateTime = null
          state.data.paymentMethod.tmbMethodInformation.otp.isOtpStatusActivated = false
        })
    }, 60000 * 3)
  },
  [SEND_OTP_VERIFICATION_FAILURE]() {
    this._vm.$swal({
      type: 'error',
      title: 'เกิดข้อผิดพลาดกรุณาลองใหม่อีกครั้ง',
    })
  },

  // VERIFY_OTP_VERIFICATION
  [VERIFY_OTP_VERIFICATION_REQUEST]() {
    this._vm.$swal({
      title: 'กรุณารอสักครู่',
      html: '<div class="loader" />',
      showConfirmButton: false,
      allowOutsideClick: false,
    })
  },
  [VERIFY_OTP_VERIFICATION_SUCCESS](state, payload) {
    const { otpStatus } = payload

    state.data.paymentMethod.tmbMethodInformation.isVerificationCompleted = true
    state.data.paymentMethod.tmbMethodInformation.otp.isOtpStatusActivated =
      otpStatus === 'Activated'

    clearTimeout(state.data.paymentMethod.tmbMethodInformation.otp.timeoutOtp)
    state.data.paymentMethod.tmbMethodInformation.otp.timeoutOtp = null
  },
  [VERIFY_OTP_VERIFICATION_FAILURE]() {
    state.data.paymentMethod.tmbMethodInformation.otp.otpAttempt -= 1

    const otpAttempt = state.data.paymentMethod.tmbMethodInformation.otp.otpAttempt

    if (otpAttempt !== 0) {
      this._vm.$swal({
        type: 'error',
        title: `รหัสไม่ถูกต้อง ท่านสามารถกรอกรหัส OTP ได้อีก ${otpAttempt} ครั้ง`,
      })
    } else {
      this._vm
        .$swal({
          type: 'error',
          title:
            'ท่านทำรายการไม่ถูกต้อง หรือไม่ได้ทำรายการภายในระยะเวลาที่กำหนด กรุณาทำรายการใหม่อีกครั้ง',
        })
        .then(() => {
          state.data.paymentMethod.tmbMethodInformation.accountNumber = null
          state.data.paymentMethod.tmbMethodInformation.accountName = null
          state.data.paymentMethod.tmbMethodInformation.accountStatus = null
          state.data.paymentMethod.tmbMethodInformation.accountType = null
          state.data.paymentMethod.tmbMethodInformation.productCode = null
          state.data.paymentMethod.tmbMethodInformation.paymentRefId = null
          state.data.paymentMethod.tmbMethodInformation.isAccountNumberCorrected = null
          state.data.paymentMethod.tmbMethodInformation.isAccountPayable = null
          state.data.paymentMethod.tmbMethodInformation.isShowOtp = false
          state.data.paymentMethod.tmbMethodInformation.otp.otpCode = null
          state.data.paymentMethod.tmbMethodInformation.otp.referenceCode = null
          state.data.paymentMethod.tmbMethodInformation.otp.token = null
          state.data.paymentMethod.tmbMethodInformation.otp.expiryDateTime = null
          state.data.paymentMethod.tmbMethodInformation.otp.isOtpStatusActivated = false
        })
    }
  },
  updateApplicationFields,
}

const actions = {
  getApplicationInformation({ commit }, applicationId) {
    commit(GET_APPLICATION_INFORMATION_REQUEST)

    this._vm.$swal({
      title: 'กรุณารอสักครู่',
      html: '<div class="loader"></div>',
      showConfirmButton: false,
      allowOutsideClick: false,
    })

    Vue.http
      .get(API.APPLICATION.GET_APPLICATION_INFORMATION(applicationId), {
        headers: {
          ...generateAuthorizationRequestHeader(),
        },
      })
      .then((res) => res.json())
      .then(
        (res) => {
          this._vm.$swal.close()

          commit(GET_APPLICATION_INFORMATION_SUCCESS, res.result)
        },
        (error) => {
          this._vm.$swal({
            type: 'error',
            title: 'เกิดข้อผิดพลาดกรุณาลองใหม่อีกครั้ง',
          })

          const { status } = error

          switch (status) {
            case 401:
              this._vm.$session.destroy()

              return (window.location.href = HOMEPAGE_URL)

            case 403:
              return this._vm.$swal({
                type: 'error',
                title: 'ขออภัย ท่านไม่ได้รับสิทธิ์ให้เข้าใช้งานส่วนนี้ได้',
              })

            case 400:
            case 500:
              return commit(GET_APPLICATION_INFORMATION_FAILURE)

            default:
              return commit(GET_APPLICATION_INFORMATION_FAILURE)
          }
        },
      )
  },
  getConsentInformation({ commit }, applicationId) {
    commit(GET_CONSENT_INFORMATION_REQUEST)

    Vue.http
      .get(API.APPLICATION.GET_CONSENT_INFORMATION(applicationId), {
        headers: {
          ...generateAuthorizationRequestHeader(),
        },
      })
      .then((res) => res.json())
      .then(
        (res) => commit(GET_CONSENT_INFORMATION_SUCCESS, res.result),
        (error) => {
          this._vm.$swal({
            type: 'error',
            title: 'เกิดข้อผิดพลาดกรุณาลองใหม่อีกครั้ง',
          })

          const { status } = error

          switch (status) {
            case 401:
              this._vm.$session.destroy()

              return (window.location.href = HOMEPAGE_URL)

            case 403:
              return this._vm.$swal({
                type: 'error',
                title: 'ขออภัย ท่านไม่ได้รับสิทธิ์ให้เข้าใช้งานส่วนนี้ได้',
              })

            case 400:
            case 500:
              return commit(GET_CONSENT_INFORMATION_FAILURE)

            default:
              return commit(GET_CONSENT_INFORMATION_FAILURE)
          }
        },
      )
  },
  updateApplicationInformation({ commit, state }, { data, submitType }) {
    commit(UPDATE_APPLICATION_INFORMATION_REQUEST)

    this._vm.$swal({
      title: 'กรุณารอสักครู่',
      html: '<h5 class="m-b-md">กำลังทำการบันทึกข้อมูลใบคำขอ</h5><div class="loader"></div>',
      showConfirmButton: false,
      allowOutsideClick: false,
    })

    const applicationId = state.data.applicationId

    Vue.http
      .post(API.APPLICATION.UPDATE_APPLICATION_INFORMATION(applicationId), data, {
        headers: {
          ...generateAuthorizationRequestHeader(),
        },
      })
      .then((res) => res.json())
      .then(
        (res) => {
          commit(UPDATE_APPLICATION_INFORMATION_SUCCESS)

          if (submitType !== 'draft') {
            if (this._vm.$session.get('chili').auth_from === 'tvi') {
              this._vm.$swal({
                type: 'error',
                title: 'ขออภัย ท่านไม่ได้รับสิทธิ์ให้เข้าใช้งานส่วนนี้ได้',
              })
            } else {
              this._vm.$swal.close()
              router.push(`/application-confirmation/${applicationId}`)
            }
          } else {
            this._vm.$swal({
              type: 'success',
              title: 'บันทึกข้อมูลเรียบร้อยแล้ว',
            })
          }
        },
        (error) => {
          this._vm.$swal({
            type: 'error',
            title: 'เกิดข้อผิดพลาดกรุณาลองใหม่อีกครั้ง',
          })

          const { status } = error

          switch (status) {
            case 401:
              this._vm.$session.destroy()

              return (window.location.href = HOMEPAGE_URL)

            case 403:
              return this._vm.$swal({
                type: 'error',
                title: 'ขออภัย ท่านไม่ได้รับสิทธิ์ให้เข้าใช้งานส่วนนี้ได้',
              })

            case 400:
            case 500:
              return commit(UPDATE_APPLICATION_INFORMATION_FAILURE)

            default:
              return commit(UPDATE_APPLICATION_INFORMATION_FAILURE)
          }
        },
      )
  },
  updatePayerInformation({ commit, state }, payerId) {
    commit(UPDATE_PAYER_INFORMATION_REQUEST)

    const quotationId = state.data.applicationInformation.quotationId

    Vue.http
      .post(
        API.APPLICATION.UPDATE_PAYER_INFORMATION(quotationId),
        {
          quotationInfo: {
            payerId,
          },
        },
        {
          headers: {
            ...generateAuthorizationRequestHeader(),
          },
        },
      )
      .then((res) => res.json())
      .then(
        (response) => commit(UPDATE_PAYER_INFORMATION_SUCCESS),
        (error) => {
          this._vm.$swal({
            type: 'error',
            title: 'เกิดข้อผิดพลาดกรุณาลองใหม่อีกครั้ง',
          })

          const { status } = error

          if (status === 401) {
            this._vm.$session.destroy()

            window.location.href = HOMEPAGE_URL
          }

          if (status === 403) {
            this._vm.$swal({
              type: 'error',
              title: 'ขออภัย ท่านไม่ได้รับสิทธิ์ให้เข้าใช้งานส่วนนี้ได้',
            })
          }

          if (status === 400) {
            commit(UPDATE_PAYER_INFORMATION_FAILURE)
          }

          if (status === 500) {
            commit(UPDATE_PAYER_INFORMATION_FAILURE)
          }
        },
      )
  },
  updateMainPayer({ commit, state }, isCombinePayment) {
    commit(UPDATE_MAIN_PAYER_REQUEST)

    const familyId = _.get(state.data.applicationInformation, 'familyQuotation.familyId', null)
    const payerId = state.data.paymentMethod.payerInformation.payerId

    Vue.http
      .post(
        API.APPLICATION.UPDATE_MAIN_PAYER(familyId),
        {
          payerId,
          isCombinePayment,
        },
        {
          headers: {
            ...generateAuthorizationRequestHeader(),
          },
        },
      )
      .then((res) => res.json())
      .then(
        (response) => commit(UPDATE_MAIN_PAYER_SUCCESS),
        (error) => {
          this._vm.$swal({
            type: 'error',
            title: 'เกิดข้อผิดพลาดกรุณาลองใหม่อีกครั้ง',
          })

          const { status } = error

          if (status === 401) {
            this._vm.$session.destroy()

            window.location.href = HOMEPAGE_URL
          }

          if (status === 403) {
            this._vm.$swal({
              type: 'error',
              title: 'ขออภัย ท่านไม่ได้รับสิทธิ์ให้เข้าใช้งานส่วนนี้ได้',
            })
          }

          if (status === 400) {
            commit(UPDATE_MAIN_PAYER_FAILURE)
          }

          if (status === 500) {
            commit(UPDATE_MAIN_PAYER_FAILURE)
          }
        },
      )
  },
  updateConsentInsuredInformation({ commit, state, dispatch }, FormData) {
    commit(UPDATE_CONSENT_INSURED_INFORMATION_REQUEST)

    this._vm.$swal({
      title: 'กรุณารอสักครู่',
      html: '<h5 class="m-b-md">กำลังทำการบันทึกข้อมูลใบคำขอ</h5><div class="loader"></div>',
      showConfirmButton: false,
      allowOutsideClick: false,
    })

    const applicationId = state.data.applicationId

    Vue.http
      .post(API.APPLICATION.UPDATE_CONSENT_INSURED(applicationId), FormData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...generateAuthorizationRequestHeader(),
        },
      })
      .then((res) => res.json())
      .then(
        (response) => {
          commit(UPDATE_CONSENT_INSURED_INFORMATION_SUCCESS)

          dispatch('updateApplicationCompleteStatus', response)
        },
        (error) => {
          this._vm.$swal({
            type: 'error',
            title: 'เกิดข้อผิดพลาดกรุณาลองใหม่อีกครั้ง',
          })

          const { status } = error

          if (status === 401) {
            this._vm.$session.destroy()

            window.location.href = HOMEPAGE_URL
          }

          if (status === 403) {
            this._vm.$swal({
              type: 'error',
              title: 'ขออภัย ท่านไม่ได้รับสิทธิ์ให้เข้าใช้งานส่วนนี้ได้',
            })
          }

          if (status === 400) {
            commit(UPDATE_CONSENT_INSURED_INFORMATION_FAILURE)
          }

          if (status === 500) {
            commit(UPDATE_CONSENT_INSURED_INFORMATION_FAILURE)
          }
        },
      )
  },
  updateConsentPayerInformation({ commit, state }, FormData) {
    commit(UPDATE_CONSENT_PAYER_INFORMATION_REQUEST)

    this._vm.$swal({
      title: 'กรุณารอสักครู่',
      html: '<h5 class="m-b-md">กำลังทำการบันทึกข้อมูล</h5><div class="loader"></div>',
      showConfirmButton: false,
      allowOutsideClick: false,
    })

    const applicationId = state.data.applicationId

    Vue.http
      .post(API.APPLICATION.UPDATE_CONSENT_PAYER(applicationId), FormData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...generateAuthorizationRequestHeader(),
        },
      })
      .then((res) => res.json())
      .then(
        () => {
          this._vm.$swal.close()

          commit(UPDATE_CONSENT_PAYER_INFORMATION_SUCCESS)

          const paymentType = _.get(state.data.applicationInformation, 'payment.paymentType', null)

          if (paymentType === 'Bill Payment') {
            return router.push(`/payment/bill-payment/${applicationId}`)
          }

          if (paymentType === 'TMB Bank Account') {
            return router.push(`/payment/direct-debit/success/${applicationId}`)
          }

          if (paymentType === 'Credit Card') {
            return router.push(`/payment/credit-card/${applicationId}`)
          }

          if (paymentType === 'QR Payment') {
            return router.push(`/payment/qrcode/${applicationId}`)
          }

          return this._vm.$swal({
            type: 'error',
            title: 'เกิดข้อผิดพลาดกรุณาลองใหม่อีกครั้ง',
          })
        },
        (error) => {
          this._vm.$swal({
            type: 'error',
            title: 'เกิดข้อผิดพลาดกรุณาลองใหม่อีกครั้ง',
          })

          const { status } = error

          if (status === 401) {
            this._vm.$session.destroy()

            window.location.href = HOMEPAGE_URL
          }

          if (status === 403) {
            this._vm.$swal({
              type: 'error',
              title: 'ขออภัย ท่านไม่ได้รับสิทธิ์ให้เข้าใช้งานส่วนนี้ได้',
            })
          }

          if (status === 400) {
            commit(UPDATE_CONSENT_PAYER_INFORMATION_FAILURE)
          }

          if (status === 500) {
            commit(UPDATE_CONSENT_PAYER_INFORMATION_FAILURE)
          }
        },
      )
  },
  updateApplicationCompleteStatus({ commit }, { applicationId, familyId }) {
    commit(UPDATE_APPLICATION_COMPLETED_STATUS_REQUEST)

    Vue.http
      .post(
        API.APPLICATION.UPDATE_APPLICATION_COMPLETE_STATUS(applicationId),
        {},
        {
          headers: {
            ...generateAuthorizationRequestHeader(),
          },
        },
      )
      .then((res) => res.json())
      .then(
        (response) => {
          this._vm.$swal.close()

          commit(UPDATE_APPLICATION_COMPLETED_STATUS_SUCCESS)

          if (familyId) {
            router.push(`/insurance_family/${familyId}`)
          } else {
            router.push(`/payment-summary/${applicationId}`)
          }
        },
        (error) => {
          const {
            status,
            body: { message },
          } = error

          if (status === 401) {
            this._vm.$session.destroy()

            window.location.href = HOMEPAGE_URL
          }

          if (status === 403) {
            this._vm.$swal({
              type: 'error',
              title: 'ขออภัย ท่านไม่ได้รับสิทธิ์ให้เข้าใช้งานส่วนนี้ได้',
            })
          }

          if (status === 400) {
            commit(UPDATE_APPLICATION_COMPLETED_STATUS_FAILURE)

            if (message) {
              this._vm.$swal({
                type: 'error',
                title: message,
              })
            } else {
              this._vm.$swal({
                type: 'error',
                title: 'เกิดข้อผิดพลาดกรุณาลองใหม่อีกครั้ง',
              })
            }
          }

          if (status === 500) {
            commit(UPDATE_APPLICATION_COMPLETED_STATUS_FAILURE)

            this._vm.$swal({
              type: 'error',
              title: 'เกิดข้อผิดพลาดกรุณาลองใหม่อีกครั้ง',
            })
          }
        },
      )
  },
  getTmbAccountInformation({ commit }, accountNo) {
    commit(GET_TMB_ACCOUNT_INFORMATION_REQUEST)

    Vue.http
      .post(
        API.PAYMENT.TMB_DIRECT_DEBIT.GET_TMB_ACCOUNT_INFORMATION,
        { accountNo },
        {
          headers: {
            ...generateAuthorizationRequestHeader(),
          },
        },
      )
      .then((res) => res.json())
      .then(
        (response) => {
          this._vm.$swal.close()

          commit(GET_TMB_ACCOUNT_INFORMATION_SUCCESS, response.result)
        },
        (error) => {
          const { status } = error

          if (status === 401) {
            this._vm.$session.destroy()

            window.location.href = HOMEPAGE_URL
          }

          if (status === 403) {
            this._vm.$swal({
              type: 'error',
              title: 'ขออภัย ท่านไม่ได้รับสิทธิ์ให้เข้าใช้งานส่วนนี้ได้',
            })
          }

          if (status === 400) {
            commit(GET_TMB_ACCOUNT_INFORMATION_FAILURE)
          }

          if (status === 500) {
            this._vm.$swal({
              type: 'error',
              title: 'เกิดข้อผิดพลาดกรุณาลองใหม่อีกครั้ง',
            })
          }
        },
      )
  },
  sendOtpVerification({ commit, state }) {
    commit(SEND_OTP_VERIFICATION_REQUEST)

    const applicationId = state.data.applicationId
    const { phone } = state.data.paymentMethod.payerInformation

    Vue.http
      .post(
        API.PAYMENT.TMB_DIRECT_DEBIT.SEND_TMB_OTP,
        { mobileNo: phone, applicationId },
        {
          headers: {
            ...generateAuthorizationRequestHeader(),
          },
        },
      )
      .then((res) => res.json())
      .then(
        (response) => {
          this._vm.$swal.close()

          commit(SEND_OTP_VERIFICATION_SUCCESS, response.result.otpResponse)
        },
        (error) => {
          const { status } = error

          if (status === 401) {
            this._vm.$session.destroy()

            window.location.href = HOMEPAGE_URL
          }

          if (status === 403) {
            this._vm.$swal({
              type: 'error',
              title: 'ขออภัย ท่านไม่ได้รับสิทธิ์ให้เข้าใช้งานส่วนนี้ได้',
            })
          }

          if (status === 400) {
            commit(SEND_OTP_VERIFICATION_FAILURE)
          }

          if (status === 500) {
            commit(SEND_OTP_VERIFICATION_FAILURE)
          }
        },
      )
  },
  verifyOtpVerification({ commit, state }) {
    const applicationId = state.data.applicationId
    const {
      paymentRefId,
      otp: { otpCode, token },
    } = state.data.paymentMethod.tmbMethodInformation

    commit(VERIFY_OTP_VERIFICATION_REQUEST)

    Vue.http
      .post(
        API.PAYMENT.TMB_DIRECT_DEBIT.VERIFY_TMB_OTP(applicationId),
        { otp: otpCode, paymentRefId, token },
        {
          headers: {
            ...generateAuthorizationRequestHeader(),
          },
        },
      )
      .then((res) => res.json())
      .then(
        (response) => {
          this._vm.$swal.close()

          commit(VERIFY_OTP_VERIFICATION_SUCCESS, response.result.otpResponse)
        },
        (error) => {
          const { status } = error

          if (status === 401) {
            this._vm.$session.destroy()

            window.location.href = HOMEPAGE_URL
          }

          if (status === 403) {
            this._vm.$swal({
              type: 'error',
              title: 'ขออภัย ท่านไม่ได้รับสิทธิ์ให้เข้าใช้งานส่วนนี้ได้',
            })
          }

          if (status === 400) {
            commit(VERIFY_OTP_VERIFICATION_FAILURE, error)
          }

          if (status === 500) {
            commit(VERIFY_OTP_VERIFICATION_FAILURE, error)
          }
        },
      )
  },
}

const getters = {
  applicationInformation: (state) => state.data,
  isLoadingApplication: (state) => state.isLoading,
  isErrorApplication: (state) => state.isError,
  applicationValidationRules: (state) => state.validation.rules,
  applicationValidationErrors: (state) => state.validation.errors,
  isDisabledApplicationForm: (state) => state.isDisabledForm,
  getApplicationFields,
}

export default {
  state,
  getters,
  mutations,
  actions,
}
